"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DollarSign, Calculator, Sparkles, ArrowRight, Play, CheckCircle2, PieChart, BarChart3, Target, Zap, LineChart, Percent, MessageSquare, Lightbulb } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { FaqSection } from "@/components/landing/FaqSection";
import { financeFaqFr, financeFaqEn } from "./faq-data";
const t = {
  fr: {
    nav: {
      login: "Connexion",
      ctaLong: "Commencer gratuit",
      ctaShort: "S'inscrire"
    },
    demo: {
      questionTitle: "Valorisation DCF",
      question: "Une entreprise génère 10M€ de FCF, croissance de 5% pendant 5 ans, puis 2% à perpétuité. WACC = 10%. Quelle est la valeur d'entreprise ?",
      candidateSteps: ["Je vais calculer la valeur en deux parties : projection + valeur terminale", "FCF années 1-5 : 10.5M, 11.0M, 11.6M, 12.2M, 12.8M", "Valeur terminale = FCF₆ / (WACC - g) = 13.1M / (10% - 2%) = 163.4M", "Actualisation au WACC de 10%... Valeur totale ≈ 142M€"],
      tips: ["Excellente décomposition du problème !", "N'oublie pas la croissance de la TV", "Vérifie toujours tes hypothèses de WACC"],
      yourReasoning: "Votre raisonnement",
      computing: "Calcul en cours...",
      dcfModel: "Modèle DCF",
      year: "Année",
      enterpriseValue: "Valeur d'entreprise",
      scoreGlobal: "Score Global",
      technical: "Technique",
      communication: "Communication",
      speed: "Rapidité"
    },
    hero: {
      badge: "Mode Finance — finition en cours · waitlist ouverte",
      titleLine1: "Préparer un entretien banque",
      titleLine2: "d'investissement avec l'IA",
      description: <>
          <strong className="text-foreground">Velyq</strong> simule les
          entretiens BFI, Private Equity, Asset Management et Big 4 audit
          pratiqués à Paris et Londres :{" "}
          <span className="font-semibold text-foreground">
            DCF, LBO, M&amp;A, comparables, accretion/dilution, brain teasers et
            fit questions (Why IB, Why Goldman)
          </span>
          . Grilles de correction calibrées sur les standards Goldman, Morgan
          Stanley, Rothschild, Lazard et BNP Paribas. Feedback chiffré sur la
          rigueur technique et la communication. Gratuit sans carte bancaire.
        </>,
      subDescription: <>
          <span className="font-medium text-foreground">
            Le mode Finance dédié arrive bientôt.
          </span>{" "}
          Inscris-toi pour être prévenu dès la sortie — en attendant, accède
          gratuitement au mode général et à tous les outils Velyq.
        </>,
      ctaPrimary: "Rejoindre la waitlist",
      ctaSecondary: "Voir les tarifs",
      prepareFor: "Prépare-toi pour :",
      liveDemo: "Démo en direct"
    },
    questionTypes: {
      title: "Tous les types de questions couverts",
      subtitle: "Des fondamentaux aux questions les plus techniques, prépare tous les formats",
      items: [{
        title: "Valorisation DCF",
        description: "Calculs de flux actualisés et valeur terminale"
      }, {
        title: "LBO Modeling",
        description: "Structure de dette, equity, IRR et multiples"
      }, {
        title: "M&A Analysis",
        description: "Synergies, accretion/dilution, comparables"
      }, {
        title: "Brain Teasers",
        description: "Probabilités, mental math, logique"
      }]
    },
    features: {
      title: "Un entraînement complet",
      items: [{
        title: "Calculs en temps réel",
        description: "Visualise tes modèles financiers pendant que tu les construis"
      }, {
        title: "Questions de fit",
        description: "Why IB? Why Goldman? Prépare aussi les questions comportementales"
      }, {
        title: "Market updates",
        description: "Questions sur l'actualité des marchés et des deals récents"
      }, {
        title: "Feedback précis",
        description: "Analyse de ta rigueur technique et de ta communication"
      }]
    },
    stats: {
      questions: "Questions techniques",
      authenticFormat: "Format authentique",
      liveCalcs: "Calculs en temps réel",
      available: "Disponible"
    },
    resources: {
      badge: "Pour aller plus loin",
      title: "Avant l'entretien finance, valide ton CV contre l'ATS.",
      subtitle: "Rothschild, Lazard, Goldman — chez les BB/élite boutiques, le CV passe un filtre ATS puis une revue manuelle ultra-codifiée. Nom d'école, deal size, league tables : voici ce que ça pondère vraiment.",
      card1Tag: "TECH · FINANCE · CONSULTING",
      card1Title: "CV ATS par métier : tech, finance, consulting",
      card1Desc: "Les grilles ATS réelles pour la finance : écoles pivot (HEC, Dauphine, Polytechnique), deals quantifiés, bulge bracket vs PE fund — comment chaque segment pondère ton CV.",
      card1Cta: "LIRE →",
      card2Tag: "DIAGNOSTIC",
      card2Title: "10 erreurs qui plantent ton score ATS",
      card2Desc: "Titre mal aligné, deals pas quantifiés, CV trop « IA ». Classées par gravité réelle avec les stats Jobscan 2025 — les erreurs qui coûtent le plus en finance.",
      card2Cta: "LIRE →",
      card3Tag: "OUTIL · GRATUIT",
      card3Title: "Analyse ATS de ton CV finance",
      card3Desc: "Teste ton CV contre une offre IB / PE / Corporate Finance réelle. Score ATS, keywords attendus, structure — sans inscription, en 30 secondes.",
      card3Cta: "ANALYSER →",
      alsoLabel: "Et aussi :",
      alsoTech: "Entretien tech",
      alsoConsulting: "Entretien consulting",
      alsoBlog: "Tous les articles"
    },
    cta: {
      title: "Sois prévenu dès la sortie du mode Finance",
      subtitle: "Le mode Finance dédié est en finition. Rejoins la waitlist pour être notifié en priorité — et commence dès maintenant avec le mode général et tous les outils Velyq.",
      button: "Rejoindre la waitlist",
      disclaimer: "Gratuit • Pas de carte bancaire • Accès immédiat aux autres modes"
    }
  },
  en: {
    nav: {
      login: "Login",
      ctaLong: "Start free",
      ctaShort: "Sign up"
    },
    demo: {
      questionTitle: "DCF Valuation",
      question: "A company generates €10M of FCF, growing 5% for 5 years, then 2% in perpetuity. WACC = 10%. What's the enterprise value?",
      candidateSteps: ["I'll compute the value in two parts: projection + terminal value", "FCF years 1-5: 10.5M, 11.0M, 11.6M, 12.2M, 12.8M", "Terminal value = FCF₆ / (WACC - g) = 13.1M / (10% - 2%) = 163.4M", "Discounting at WACC of 10%... Total value ≈ €142M"],
      tips: ["Excellent problem breakdown!", "Don't forget terminal value growth", "Always sanity-check your WACC assumptions"],
      yourReasoning: "Your reasoning",
      computing: "Computing...",
      dcfModel: "DCF Model",
      year: "Year",
      enterpriseValue: "Enterprise value",
      scoreGlobal: "Overall Score",
      technical: "Technical",
      communication: "Communication",
      speed: "Speed"
    },
    hero: {
      badge: "Finance Mode — polishing · waitlist open",
      titleLine1: "Prepare for an investment",
      titleLine2: "banking interview with AI",
      description: <>
          <strong className="text-foreground">Velyq</strong> simulates IB,
          Private Equity, Asset Management and Big 4 audit interviews practiced
          in Paris and London:{" "}
          <span className="font-semibold text-foreground">
            DCF, LBO, M&amp;A, comparables, accretion/dilution, brain teasers and
            fit questions (Why IB, Why Goldman)
          </span>
          . Scoring grids calibrated on Goldman, Morgan Stanley, Rothschild,
          Lazard and BNP Paribas standards. Quantified feedback on technical
          rigor and communication. Free, no credit card.
        </>,
      subDescription: <>
          <span className="font-medium text-foreground">
            The dedicated Finance mode is coming soon.
          </span>{" "}
          Sign up to be notified at launch — in the meantime, access the general
          mode and every Velyq tool for free.
        </>,
      ctaPrimary: "Join the waitlist",
      ctaSecondary: "View pricing",
      prepareFor: "Prepare for:",
      liveDemo: "Live demo"
    },
    questionTypes: {
      title: "Every question type covered",
      subtitle: "From fundamentals to the most technical questions, prepare every format",
      items: [{
        title: "DCF Valuation",
        description: "Discounted cash flows and terminal value calculations"
      }, {
        title: "LBO Modeling",
        description: "Debt structure, equity, IRR and multiples"
      }, {
        title: "M&A Analysis",
        description: "Synergies, accretion/dilution, comparables"
      }, {
        title: "Brain Teasers",
        description: "Probabilities, mental math, logic"
      }]
    },
    features: {
      title: "Complete training",
      items: [{
        title: "Real-time calculations",
        description: "Visualize your financial models as you build them"
      }, {
        title: "Fit questions",
        description: "Why IB? Why Goldman? Also prepare behavioral questions"
      }, {
        title: "Market updates",
        description: "Questions on market news and recent deals"
      }, {
        title: "Precise feedback",
        description: "Analysis of your technical rigor and communication"
      }]
    },
    stats: {
      questions: "Technical questions",
      authenticFormat: "Authentic format",
      liveCalcs: "Live calculations",
      available: "Available"
    },
    resources: {
      badge: "Go further",
      title: "Before the finance interview, validate your resume against the ATS.",
      subtitle: "Rothschild, Lazard, Goldman — at BB / elite boutiques, the resume goes through an ATS filter then an ultra-codified manual review. School name, deal size, league tables: here's what actually carries weight.",
      card1Tag: "TECH · FINANCE · CONSULTING",
      card1Title: "ATS resumes by sector: tech, finance, consulting",
      card1Desc: "Real ATS grids for finance: pivot schools (HEC, Dauphine, Polytechnique), quantified deals, bulge bracket vs PE fund — how each segment weights your resume.",
      card1Cta: "READ →",
      card2Tag: "DIAGNOSIS",
      card2Title: "10 mistakes that crash your ATS score",
      card2Desc: "Misaligned title, unquantified deals, overly-AI resume. Ranked by real severity with Jobscan 2025 stats — the mistakes that cost the most in finance.",
      card2Cta: "READ →",
      card3Tag: "TOOL · FREE",
      card3Title: "ATS analysis of your finance resume",
      card3Desc: "Test your resume against a real IB / PE / Corporate Finance offer. ATS score, expected keywords, structure — no signup, 30 seconds.",
      card3Cta: "ANALYZE →",
      alsoLabel: "Also see:",
      alsoTech: "Tech interview",
      alsoConsulting: "Consulting interview",
      alsoBlog: "All articles"
    },
    cta: {
      title: "Get notified when Finance mode launches",
      subtitle: "The dedicated Finance mode is in polish. Join the waitlist to be notified first — and start now with the general mode and every Velyq tool.",
      button: "Join the waitlist",
      disclaimer: "Free • No credit card • Immediate access to other modes"
    }
  }
};

// Finance Interview Demo Component
function FinanceInterviewDemo({
  locale
}: {
  locale: "fr" | "en";
}) {
  const content = locale === "fr" ? t.fr : t.en;
  const [step, setStep] = useState(0);
  const [showCalculation, setShowCalculation] = useState(false);
  const [score, setScore] = useState({
    technical: 0,
    communication: 0,
    speed: 0
  });
  const [tip, setTip] = useState<string | null>(null);
  const [loopKey, setLoopKey] = useState(0);
  const question = {
    title: content.demo.questionTitle,
    question: content.demo.question
  };
  const candidateSteps = content.demo.candidateSteps.map((text, i) => ({
    text,
    delay: 2000 + i * 2000
  }));
  const calculation = {
    fcf: [10.5, 11.0, 11.6, 12.2, 12.8],
    discountFactors: [0.91, 0.83, 0.75, 0.68, 0.62],
    pvFcf: [9.5, 9.1, 8.7, 8.3, 7.9],
    terminalValue: 163.4,
    pvTerminal: 101.5,
    totalEV: 145.0
  };
  const tips = content.demo.tips;
  useEffect(() => {
    setStep(0);
    setShowCalculation(false);
    setScore({
      technical: 0,
      communication: 0,
      speed: 0
    });
    setTip(null);
    const timers = candidateSteps.map((s, i) => setTimeout(() => setStep(i + 1), s.delay));
    const calcTimer = setTimeout(() => setShowCalculation(true), 5000);
    const scoreTimer = setTimeout(() => {
      setScore({
        technical: 92,
        communication: 85,
        speed: 88
      });
    }, 9000);
    const tipTimer = setTimeout(() => {
      setTip(tips[Math.floor(Math.random() * tips.length)]);
    }, 10000);
    const loopTimer = setTimeout(() => setLoopKey(k => k + 1), 14000);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(calcTimer);
      clearTimeout(scoreTimer);
      clearTimeout(tipTimer);
      clearTimeout(loopTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loopKey]);
  const overallScore = Math.round((score.technical + score.communication + score.speed) / 3);
  return <div className="grid md:grid-cols-2 gap-4">
      {/* Question + Response Panel */}
      <div className="space-y-4">
        {/* Question */}
        <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-green-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <Badge className="mb-2 bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
                  {question.title}
                </Badge>
                <p className="text-sm">{question.question}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Candidate Response */}
        <Card className="border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Calculator className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm font-medium">{content.demo.yourReasoning}</span>
            </div>
            <div className="space-y-2">
              {candidateSteps.map((s, i) => <div key={i} className={`text-sm p-2 rounded-lg transition-all duration-500 ${i < step ? "bg-muted opacity-100" : "opacity-0 h-0 overflow-hidden"}`}>
                  {s.text}
                </div>)}
              {step < candidateSteps.length && <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  {content.demo.computing}
                </div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calculation + Scores Panel */}
      <div className="space-y-4">
        {/* Live Calculation Sheet */}
        <Card className={`border-emerald-500/30 bg-[#0d1117] transition-all duration-500 ${showCalculation ? "opacity-100" : "opacity-40"}`}>
          <CardContent className="p-4 font-mono text-xs">
            <div className="flex items-center gap-2 mb-3">
              <LineChart className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-sans text-sm font-medium">{content.demo.dcfModel}</span>
            </div>

            {/* Mini spreadsheet */}
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-500 border-b border-zinc-700">
                    <th className="text-left py-1">{content.demo.year}</th>
                    {[1, 2, 3, 4, 5].map(y => <th key={y} className="text-right py-1 px-2">{y}</th>)}
                    <th className="text-right py-1 px-2 text-emerald-400">TV</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800">
                    <td className="py-1 text-zinc-500">FCF (M€)</td>
                    {calculation.fcf.map((v, i) => <td key={i} className="text-right py-1 px-2">{v.toFixed(1)}</td>)}
                    <td className="text-right py-1 px-2 text-emerald-400">13.1</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-1 text-zinc-500">Discount</td>
                    {calculation.discountFactors.map((v, i) => <td key={i} className="text-right py-1 px-2 text-zinc-500">{v.toFixed(2)}</td>)}
                    <td className="text-right py-1 px-2 text-zinc-500">0.62</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-zinc-500">PV (M€)</td>
                    {calculation.pvFcf.map((v, i) => <td key={i} className="text-right py-1 px-2 text-blue-400">{v.toFixed(1)}</td>)}
                    <td className="text-right py-1 px-2 text-emerald-400">{calculation.pvTerminal.toFixed(1)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-3 pt-3 border-t border-zinc-700 flex justify-between items-center">
              <span className="text-zinc-400">{content.demo.enterpriseValue}</span>
              <span className="text-lg font-bold text-emerald-400">{calculation.totalEV.toFixed(0)}M€</span>
            </div>
          </CardContent>
        </Card>

        {/* Scores */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                {content.demo.scoreGlobal}
              </span>
              <span className={`text-2xl font-bold ${overallScore >= 80 ? "text-green-500" : "text-yellow-500"}`}>
                {overallScore || "—"}
              </span>
            </div>
            <Progress value={overallScore} className="h-2" />

            <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
              <div className="text-center">
                <div className="text-muted-foreground">{content.demo.technical}</div>
                <div className="font-bold text-emerald-500">{score.technical || "—"}</div>
              </div>
              <div className="text-center">
                <div className="text-muted-foreground">{content.demo.communication}</div>
                <div className="font-bold text-blue-500">{score.communication || "—"}</div>
              </div>
              <div className="text-center">
                <div className="text-muted-foreground">{content.demo.speed}</div>
                <div className="font-bold text-primary">{score.speed || "—"}</div>
              </div>
            </div>

            {tip && <div className="mt-3 p-2 bg-green-500/10 rounded-lg border border-green-500/20 animate-in fade-in">
                <div className="flex items-start gap-2 text-xs text-green-600 dark:text-green-400">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  {tip}
                </div>
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}
const companies = [{
  name: "Goldman Sachs",
  color: "text-blue-400"
}, {
  name: "Morgan Stanley",
  color: "text-blue-500"
}, {
  name: "J.P. Morgan",
  color: "text-zinc-400"
}, {
  name: "Lazard",
  color: "text-emerald-500"
}, {
  name: "Rothschild",
  color: "text-primary"
}, {
  name: "BNP Paribas",
  color: "text-green-500"
}];
const questionTypeStyles = [{
  icon: LineChart,
  color: "from-emerald-500/10 to-emerald-500/5 border-emerald-500/20",
  iconColor: "text-emerald-500"
}, {
  icon: BarChart3,
  color: "from-blue-500/10 to-blue-500/5 border-blue-500/20",
  iconColor: "text-blue-500"
}, {
  icon: PieChart,
  color: "from-purple-500/10 to-purple-500/5 border-purple-500/20",
  iconColor: "text-purple-500"
}, {
  icon: Percent,
  color: "from-primary/10 to-primary/5 border-primary/20",
  iconColor: "text-primary"
}];
const featureIcons = [Calculator, MessageSquare, Lightbulb, Target];
export default function FinanceInterviewPage() {
  const $ = _c(192);
  const {
    locale
  } = useTranslation();
  const content = locale === "fr" ? t.fr : t.en;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/"><Logo /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LanguageSwitcher />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== content.nav.login) {
    t2 = <Link href="/login" className="hidden sm:block"><Button variant="ghost" size="sm">{content.nav.login}</Button></Link>;
    $[2] = content.nav.login;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== content.nav.ctaLong) {
    t3 = <span className="hidden sm:inline">{content.nav.ctaLong}</span>;
    $[4] = content.nav.ctaLong;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== content.nav.ctaShort) {
    t4 = <span className="sm:hidden">{content.nav.ctaShort}</span>;
    $[6] = content.nav.ctaShort;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Link href="/register"><Button size="sm">{t3}{t4}</Button></Link>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50"><div className="container mx-auto px-4 h-16 flex items-center justify-between">{t0}<div className="flex items-center gap-2 sm:gap-4">{t1}{t2}{t5}</div></div></header>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Sparkles className="w-3 h-3 mr-1" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== content.hero.badge) {
    t8 = <Badge className="mb-4 bg-primary/10 text-primary border border-primary/30">{t7}{content.hero.badge}</Badge>;
    $[15] = content.hero.badge;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <br />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== content.hero.titleLine1 || $[19] !== content.hero.titleLine2) {
    t10 = <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">{content.hero.titleLine1}{t9}{content.hero.titleLine2}</h1>;
    $[18] = content.hero.titleLine1;
    $[19] = content.hero.titleLine2;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== content.hero.description) {
    t11 = <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-4">{content.hero.description}</p>;
    $[21] = content.hero.description;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== content.hero.subDescription) {
    t12 = <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-8">{content.hero.subDescription}</p>;
    $[23] = content.hero.subDescription;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Play className="w-5 h-5" />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== content.hero.ctaPrimary) {
    t14 = <Link href="/register?waitlist=finance"><Button size="lg" className="gap-2">{t13}{content.hero.ctaPrimary}</Button></Link>;
    $[26] = content.hero.ctaPrimary;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ArrowRight className="w-4 h-4" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== content.hero.ctaSecondary) {
    t16 = <Link href="/pricing"><Button size="lg" variant="outline" className="gap-2">{content.hero.ctaSecondary}{t15}</Button></Link>;
    $[29] = content.hero.ctaSecondary;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t16) {
    t17 = <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">{t14}{t16}</div>;
    $[31] = t14;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t17 || $[38] !== t8) {
    t18 = <div className="text-center mb-12">{t8}{t10}{t11}{t12}{t17}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t17;
    $[38] = t8;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== content.hero.prepareFor) {
    t19 = <span>{content.hero.prepareFor}</span>;
    $[40] = content.hero.prepareFor;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = companies.map(_temp);
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t19) {
    t21 = <div className="flex flex-wrap justify-center items-center gap-6 mb-16 text-sm text-muted-foreground">{t19}{t20}</div>;
    $[43] = t19;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Zap className="w-3 h-3" />;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== content.hero.liveDemo) {
    t23 = <div className="text-center mb-6"><Badge variant="outline" className="gap-1">{t22}{content.hero.liveDemo}</Badge></div>;
    $[46] = content.hero.liveDemo;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== locale) {
    t24 = <FinanceInterviewDemo locale={locale} />;
    $[48] = locale;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t23 || $[51] !== t24) {
    t25 = <div className="mb-8">{t23}{t24}</div>;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t18 || $[54] !== t21 || $[55] !== t25) {
    t26 = <section className="py-20 px-4"><div className="container mx-auto max-w-6xl">{t18}{t21}{t25}</div></section>;
    $[53] = t18;
    $[54] = t21;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== content.questionTypes.title) {
    t27 = <h2 className="text-3xl font-bold text-center mb-4">{content.questionTypes.title}</h2>;
    $[57] = content.questionTypes.title;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== content.questionTypes.subtitle) {
    t28 = <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">{content.questionTypes.subtitle}</p>;
    $[59] = content.questionTypes.subtitle;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== content.questionTypes.items) {
    t29 = content.questionTypes.items.map(_temp2);
    $[61] = content.questionTypes.items;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t29) {
    t30 = <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">{t29}</div>;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t27 || $[66] !== t28 || $[67] !== t30) {
    t31 = <section className="py-20 px-4 bg-muted/30"><div className="container mx-auto max-w-6xl">{t27}{t28}{t30}</div></section>;
    $[65] = t27;
    $[66] = t28;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== content.features.title) {
    t32 = <h2 className="text-3xl font-bold text-center mb-12">{content.features.title}</h2>;
    $[69] = content.features.title;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== content.features.items) {
    t33 = content.features.items.map(_temp3);
    $[71] = content.features.items;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t33) {
    t34 = <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">{t33}</div>;
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t32 || $[76] !== t34) {
    t35 = <section className="py-20 px-4"><div className="container mx-auto max-w-6xl">{t32}{t34}</div></section>;
    $[75] = t32;
    $[76] = t34;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="text-4xl font-bold text-primary mb-2">300+</div>;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== content.stats.questions) {
    t37 = <div>{t36}<div className="text-sm text-muted-foreground">{content.stats.questions}</div></div>;
    $[79] = content.stats.questions;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="text-4xl font-bold text-primary mb-2">IB/PE</div>;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  let t39;
  if ($[82] !== content.stats.authenticFormat) {
    t39 = <div>{t38}<div className="text-sm text-muted-foreground">{content.stats.authenticFormat}</div></div>;
    $[82] = content.stats.authenticFormat;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="text-4xl font-bold text-primary mb-2">Live</div>;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== content.stats.liveCalcs) {
    t41 = <div>{t40}<div className="text-sm text-muted-foreground">{content.stats.liveCalcs}</div></div>;
    $[85] = content.stats.liveCalcs;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="text-4xl font-bold text-primary mb-2">24/7</div>;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== content.stats.available) {
    t43 = <div>{t42}<div className="text-sm text-muted-foreground">{content.stats.available}</div></div>;
    $[88] = content.stats.available;
    $[89] = t43;
  } else {
    t43 = $[89];
  }
  let t44;
  if ($[90] !== t37 || $[91] !== t39 || $[92] !== t41 || $[93] !== t43) {
    t44 = <section className="py-20 px-4 bg-muted/30"><div className="container mx-auto max-w-4xl"><div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">{t37}{t39}{t41}{t43}</div></div></section>;
    $[90] = t37;
    $[91] = t39;
    $[92] = t41;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== content.resources.badge) {
    t45 = <Badge variant="outline" className="mb-4">{content.resources.badge}</Badge>;
    $[95] = content.resources.badge;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== content.resources.title) {
    t46 = <h2 className="text-3xl font-bold mb-4">{content.resources.title}</h2>;
    $[97] = content.resources.title;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== content.resources.subtitle) {
    t47 = <p className="text-muted-foreground max-w-2xl mx-auto">{content.resources.subtitle}</p>;
    $[99] = content.resources.subtitle;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== t45 || $[102] !== t46 || $[103] !== t47) {
    t48 = <div className="text-center mb-12">{t45}{t46}{t47}</div>;
    $[101] = t45;
    $[102] = t46;
    $[103] = t47;
    $[104] = t48;
  } else {
    t48 = $[104];
  }
  let t49;
  if ($[105] !== content.resources.card1Tag) {
    t49 = <div className="text-xs font-mono text-primary mb-3">{content.resources.card1Tag}</div>;
    $[105] = content.resources.card1Tag;
    $[106] = t49;
  } else {
    t49 = $[106];
  }
  let t50;
  if ($[107] !== content.resources.card1Title) {
    t50 = <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{content.resources.card1Title}</h3>;
    $[107] = content.resources.card1Title;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== content.resources.card1Desc) {
    t51 = <p className="text-sm text-muted-foreground mb-4">{content.resources.card1Desc}</p>;
    $[109] = content.resources.card1Desc;
    $[110] = t51;
  } else {
    t51 = $[110];
  }
  let t52;
  if ($[111] !== content.resources.card1Cta) {
    t52 = <span className="text-xs font-mono text-primary">{content.resources.card1Cta}</span>;
    $[111] = content.resources.card1Cta;
    $[112] = t52;
  } else {
    t52 = $[112];
  }
  let t53;
  if ($[113] !== t49 || $[114] !== t50 || $[115] !== t51 || $[116] !== t52) {
    t53 = <Link href="/blog/cv-ats-tech-finance-consulting" className="group"><Card className="h-full hover:border-primary/50 transition-colors"><CardContent className="pt-6">{t49}{t50}{t51}{t52}</CardContent></Card></Link>;
    $[113] = t49;
    $[114] = t50;
    $[115] = t51;
    $[116] = t52;
    $[117] = t53;
  } else {
    t53 = $[117];
  }
  let t54;
  if ($[118] !== content.resources.card2Tag) {
    t54 = <div className="text-xs font-mono text-primary mb-3">{content.resources.card2Tag}</div>;
    $[118] = content.resources.card2Tag;
    $[119] = t54;
  } else {
    t54 = $[119];
  }
  let t55;
  if ($[120] !== content.resources.card2Title) {
    t55 = <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{content.resources.card2Title}</h3>;
    $[120] = content.resources.card2Title;
    $[121] = t55;
  } else {
    t55 = $[121];
  }
  let t56;
  if ($[122] !== content.resources.card2Desc) {
    t56 = <p className="text-sm text-muted-foreground mb-4">{content.resources.card2Desc}</p>;
    $[122] = content.resources.card2Desc;
    $[123] = t56;
  } else {
    t56 = $[123];
  }
  let t57;
  if ($[124] !== content.resources.card2Cta) {
    t57 = <span className="text-xs font-mono text-primary">{content.resources.card2Cta}</span>;
    $[124] = content.resources.card2Cta;
    $[125] = t57;
  } else {
    t57 = $[125];
  }
  let t58;
  if ($[126] !== t54 || $[127] !== t55 || $[128] !== t56 || $[129] !== t57) {
    t58 = <Link href="/blog/erreurs-cv-ats-score-faible" className="group"><Card className="h-full hover:border-primary/50 transition-colors"><CardContent className="pt-6">{t54}{t55}{t56}{t57}</CardContent></Card></Link>;
    $[126] = t54;
    $[127] = t55;
    $[128] = t56;
    $[129] = t57;
    $[130] = t58;
  } else {
    t58 = $[130];
  }
  let t59;
  if ($[131] !== content.resources.card3Tag) {
    t59 = <div className="text-xs font-mono text-primary mb-3">{content.resources.card3Tag}</div>;
    $[131] = content.resources.card3Tag;
    $[132] = t59;
  } else {
    t59 = $[132];
  }
  let t60;
  if ($[133] !== content.resources.card3Title) {
    t60 = <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{content.resources.card3Title}</h3>;
    $[133] = content.resources.card3Title;
    $[134] = t60;
  } else {
    t60 = $[134];
  }
  let t61;
  if ($[135] !== content.resources.card3Desc) {
    t61 = <p className="text-sm text-muted-foreground mb-4">{content.resources.card3Desc}</p>;
    $[135] = content.resources.card3Desc;
    $[136] = t61;
  } else {
    t61 = $[136];
  }
  let t62;
  if ($[137] !== content.resources.card3Cta) {
    t62 = <span className="text-xs font-mono text-primary">{content.resources.card3Cta}</span>;
    $[137] = content.resources.card3Cta;
    $[138] = t62;
  } else {
    t62 = $[138];
  }
  let t63;
  if ($[139] !== t59 || $[140] !== t60 || $[141] !== t61 || $[142] !== t62) {
    t63 = <Link href="/analyse-cv" className="group"><Card className="h-full hover:border-primary/50 transition-colors"><CardContent className="pt-6">{t59}{t60}{t61}{t62}</CardContent></Card></Link>;
    $[139] = t59;
    $[140] = t60;
    $[141] = t61;
    $[142] = t62;
    $[143] = t63;
  } else {
    t63 = $[143];
  }
  let t64;
  if ($[144] !== t53 || $[145] !== t58 || $[146] !== t63) {
    t64 = <div className="grid md:grid-cols-3 gap-6 mb-10">{t53}{t58}{t63}</div>;
    $[144] = t53;
    $[145] = t58;
    $[146] = t63;
    $[147] = t64;
  } else {
    t64 = $[147];
  }
  let t65;
  if ($[148] !== content.resources.alsoLabel) {
    t65 = <span className="text-xs text-muted-foreground font-mono self-center mr-2">{content.resources.alsoLabel}</span>;
    $[148] = content.resources.alsoLabel;
    $[149] = t65;
  } else {
    t65 = $[149];
  }
  let t66;
  if ($[150] !== content.resources.alsoTech) {
    t66 = <Link href="/entretien-tech"><Badge variant="outline" className="hover:border-primary cursor-pointer">{content.resources.alsoTech}</Badge></Link>;
    $[150] = content.resources.alsoTech;
    $[151] = t66;
  } else {
    t66 = $[151];
  }
  let t67;
  if ($[152] !== content.resources.alsoConsulting) {
    t67 = <Link href="/entretien-consulting"><Badge variant="outline" className="hover:border-primary cursor-pointer">{content.resources.alsoConsulting}</Badge></Link>;
    $[152] = content.resources.alsoConsulting;
    $[153] = t67;
  } else {
    t67 = $[153];
  }
  let t68;
  if ($[154] !== content.resources.alsoBlog) {
    t68 = <Link href="/blog"><Badge variant="outline" className="hover:border-primary cursor-pointer">{content.resources.alsoBlog}</Badge></Link>;
    $[154] = content.resources.alsoBlog;
    $[155] = t68;
  } else {
    t68 = $[155];
  }
  let t69;
  if ($[156] !== t65 || $[157] !== t66 || $[158] !== t67 || $[159] !== t68) {
    t69 = <div className="flex flex-wrap gap-3 justify-center pt-6 border-t border-border">{t65}{t66}{t67}{t68}</div>;
    $[156] = t65;
    $[157] = t66;
    $[158] = t67;
    $[159] = t68;
    $[160] = t69;
  } else {
    t69 = $[160];
  }
  let t70;
  if ($[161] !== t48 || $[162] !== t64 || $[163] !== t69) {
    t70 = <section className="py-20 px-4"><div className="container mx-auto max-w-5xl">{t48}{t64}{t69}</div></section>;
    $[161] = t48;
    $[162] = t64;
    $[163] = t69;
    $[164] = t70;
  } else {
    t70 = $[164];
  }
  const t71 = locale === "fr" ? financeFaqFr : financeFaqEn;
  const t72 = locale === "fr" ? "Questions fr\xE9quentes" : "Frequently asked questions";
  let t73;
  if ($[165] !== t71 || $[166] !== t72) {
    t73 = <FaqSection items={t71} title={t72} />;
    $[165] = t71;
    $[166] = t72;
    $[167] = t73;
  } else {
    t73 = $[167];
  }
  let t74;
  if ($[168] !== content.cta.title) {
    t74 = <h2 className="text-3xl font-bold mb-4">{content.cta.title}</h2>;
    $[168] = content.cta.title;
    $[169] = t74;
  } else {
    t74 = $[169];
  }
  let t75;
  if ($[170] !== content.cta.subtitle) {
    t75 = <p className="text-muted-foreground mb-8 max-w-xl mx-auto">{content.cta.subtitle}</p>;
    $[170] = content.cta.subtitle;
    $[171] = t75;
  } else {
    t75 = $[171];
  }
  let t76;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = <ArrowRight className="w-4 h-4" />;
    $[172] = t76;
  } else {
    t76 = $[172];
  }
  let t77;
  if ($[173] !== content.cta.button) {
    t77 = <Link href="/register?waitlist=finance"><Button size="lg" className="gap-2">{content.cta.button}{t76}</Button></Link>;
    $[173] = content.cta.button;
    $[174] = t77;
  } else {
    t77 = $[174];
  }
  let t78;
  if ($[175] !== content.cta.disclaimer) {
    t78 = <p className="text-xs text-muted-foreground mt-4">{content.cta.disclaimer}</p>;
    $[175] = content.cta.disclaimer;
    $[176] = t78;
  } else {
    t78 = $[176];
  }
  let t79;
  if ($[177] !== t74 || $[178] !== t75 || $[179] !== t77 || $[180] !== t78) {
    t79 = <section className="py-20 px-4"><div className="container mx-auto max-w-4xl text-center">{t74}{t75}{t77}{t78}</div></section>;
    $[177] = t74;
    $[178] = t75;
    $[179] = t77;
    $[180] = t78;
    $[181] = t79;
  } else {
    t79 = $[181];
  }
  let t80;
  if ($[182] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <Footer />;
    $[182] = t80;
  } else {
    t80 = $[182];
  }
  let t81;
  if ($[183] !== t26 || $[184] !== t31 || $[185] !== t35 || $[186] !== t44 || $[187] !== t6 || $[188] !== t70 || $[189] !== t73 || $[190] !== t79) {
    t81 = <div className="min-h-screen bg-background">{t6}{t26}{t31}{t35}{t44}{t70}{t73}{t79}{t80}</div>;
    $[183] = t26;
    $[184] = t31;
    $[185] = t35;
    $[186] = t44;
    $[187] = t6;
    $[188] = t70;
    $[189] = t73;
    $[190] = t79;
    $[191] = t81;
  } else {
    t81 = $[191];
  }
  return t81;
}
function _temp3(feature, i_0) {
  const Icon_0 = featureIcons[i_0];
  return <Card key={i_0} className="border-2 hover:border-primary/50 transition-colors"><CardContent className="pt-6"><Icon_0 className="w-10 h-10 text-primary mb-4" /><h3 className="font-semibold mb-2">{feature.title}</h3><p className="text-sm text-muted-foreground">{feature.description}</p></CardContent></Card>;
}
function _temp2(type, i) {
  const style = questionTypeStyles[i];
  const Icon = style.icon;
  return <Card key={i} className={`bg-gradient-to-br ${style.color}`}><CardContent className="pt-6"><Icon className={`w-8 h-8 ${style.iconColor} mb-4`} /><h3 className="font-semibold mb-2">{type.title}</h3><p className="text-sm text-muted-foreground">{type.description}</p></CardContent></Card>;
}
function _temp(company) {
  return <span key={company.name} className={`font-semibold ${company.color}`}>{company.name}</span>;
}